import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslatePipe } from '../../../../core/pipes/translate.pipe';
import { RoundToTenthPipe } from '../../../../core/pipes/round-to-tenth.pipe';

@Component({
  selector: 'app-unit-info',
  standalone: true,
  imports: [CommonModule, TranslatePipe, RoundToTenthPipe],
  template: `
    <div class="p-4 sm:p-8">
      <div class="mb-6">
        <h1 class="text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight text-text-primary break-words">
          {{ title }}
        </h1>
        <div class="flex items-start sm:items-center gap-1.5 text-text-muted mt-2.5 group min-w-0">
          <i
            class="fa-solid fa-location-dot text-primary text-xs sm:text-sm group-hover:animate-bounce"
          ></i>
          <span class="body-small font-bold tracking-wide font-mono break-words min-w-0">{{
            location
          }}</span>
        </div>
      </div>

      <div class="grid grid-cols-1 min-[380px]:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 pb-6 border-b border-border-subtle">
        <div class="flex items-center gap-3 p-3 bg-bg-secondary border border-border-subtle rounded-2xl min-w-0">
          <div
            class="w-9 h-9 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary"
          >
            <i class="fa-solid fa-bed text-sm"></i>
          </div>
          <div class="min-w-0">
            <div class="caption font-black uppercase tracking-wider font-mono">
              {{ 'unitDetails.info.bedrooms' | translate }}
            </div>
            <div class="body-small font-black text-text-primary font-mono mt-0.5">
              {{ bedrooms | roundToTenth }} {{ 'unitDetails.info.rooms' | translate }}
            </div>
          </div>
        </div>

        <div class="flex items-center gap-3 p-3 bg-bg-secondary border border-border-subtle rounded-2xl min-w-0">
          <div
            class="w-9 h-9 rounded-xl bg-info/10 border border-info/20 flex items-center justify-center text-info"
          >
            <i class="fa-solid fa-bath text-sm"></i>
          </div>
          <div class="min-w-0">
            <div class="caption font-black uppercase tracking-wider font-mono">
              {{ 'unitDetails.info.bathrooms' | translate }}
            </div>
            <div class="body-small font-black text-text-primary font-mono mt-0.5">
              {{ bathrooms | roundToTenth }} {{ 'unitDetails.info.baths' | translate }}
            </div>
          </div>
        </div>

        <div class="flex items-center gap-3 p-3 bg-bg-secondary border border-border-subtle rounded-2xl min-w-0">
          <div
            class="w-9 h-9 rounded-xl bg-success/10 border border-success/20 flex items-center justify-center text-success"
          >
            <i class="fa-solid fa-vector-square text-sm"></i>
          </div>
          <div class="min-w-0">
            <div class="caption font-black uppercase tracking-wider font-mono">
              {{ 'unitDetails.info.totalArea' | translate }}
            </div>
            <div class="body-small font-black text-text-primary font-mono mt-0.5">
              {{ area | roundToTenth }}
            </div>
          </div>
        </div>

        <div class="flex items-center gap-3 p-3 bg-bg-secondary border border-border-subtle rounded-2xl min-w-0">
          <div
            class="w-9 h-9 rounded-xl bg-warning/10 border border-warning/20 flex items-center justify-center text-warning"
          >
            <i class="fa-solid fa-star text-sm shadow-sm"></i>
          </div>
          <div class="min-w-0">
            <div class="caption font-black uppercase tracking-wider font-mono">
              {{ 'unitDetails.info.ratingScore' | translate }}
            </div>
            <div class="body-small font-black text-text-primary font-mono mt-0.5">
              {{ rating | roundToTenth }}
              <span class="text-text-muted font-normal text-[11px] block sm:inline">({{ reviews | roundToTenth }} {{ 'unitDetails.info.reviews' | translate }})</span>
            </div>
          </div>
        </div>
      </div>

      <div class="grid grid-cols-1 min-[420px]:grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-5 py-5 border-b border-border-subtle font-mono">
        @if (unitType) {
          <div>
            <div class="caption font-black uppercase tracking-widest text-text-muted">
              Unit Type
            </div>
            <div class="body-small font-black text-text-secondary mt-1 flex items-center gap-1.5">
              <i class="fa-solid fa-building text-text-muted text-[11px]"></i>
              <span class="break-words min-w-0">{{ unitType }}</span>
            </div>
          </div>
        }
        @if (unitNumber) {
          <div>
            <div class="caption font-black uppercase tracking-widest text-text-muted">
              Unit Number
            </div>
            <div class="body-small font-black text-text-secondary mt-1 flex items-center gap-1.5">
              <i class="fa-solid fa-hashtag text-text-muted text-[11px]"></i>
              <span class="break-words min-w-0">{{ unitNumber }}</span>
            </div>
          </div>
        }
        @if (adults > 0 || children > 0) {
          <div>
            <div class="caption font-black uppercase tracking-widest text-text-muted">
              Capacity
            </div>
            <div class="body-small font-black text-text-secondary mt-1 flex items-center gap-1.5">
              <i class="fa-solid fa-users text-text-muted text-[11px]"></i>
              <span class="break-words min-w-0">{{ adults }} Adults, {{ children }} Children</span>
            </div>
          </div>
        }
        <div>
          <div class="caption font-black uppercase tracking-widest text-text-muted">
            {{ 'unitDetails.info.floorLevel' | translate }}
          </div>
          <div class="body-small font-black text-text-secondary mt-1 flex items-center gap-1.5">
            <i class="fa-solid fa-layer-group text-text-muted text-[11px]"></i>
            <span class="break-words min-w-0">{{ floor | roundToTenth }}</span>
          </div>
        </div>
        <div>
          <div class="caption font-black uppercase tracking-widest text-text-muted">
            {{ 'unitDetails.info.furnishing' | translate }}
          </div>
          <div
            class="body-small font-black mt-1 flex items-center gap-1.5"
            [ngClass]="furnished ? 'text-success' : 'text-text-muted'"
          >
            <i
              [class]="furnished ? 'fa-solid fa-couch' : 'fa-solid fa-border-none'"
              class="text-[11px]"
            ></i>
            <span class="break-words min-w-0">{{ furnished ? ('unitDetails.info.furnished' | translate) : ('unitDetails.info.unfurnished' | translate) }}</span>
          </div>
        </div>
        @if (yearBuilt) {
          <div>
            <div class="caption font-black uppercase tracking-widest text-text-muted">
              {{ 'unitDetails.info.yearBuilt' | translate }}
            </div>
            <div
              class="body-small font-black text-text-secondary mt-1 flex items-center gap-1.5"
            >
              <i class="fa-solid fa-calendar text-text-muted text-[11px]"></i>
              <span class="break-words min-w-0">{{ yearBuilt }}</span>
            </div>
          </div>
        }
      </div>

      <div class="py-5 border-b border-border-subtle">
        <h3 class="caption font-black uppercase tracking-widest font-mono mb-2.5">
          {{ 'unitDetails.info.description' | translate }}
        </h3>
        <p class="body text-sm sm:text-base text-start sm:text-justify font-medium break-words">
          {{ description }}
        </p>
      </div>

      <div class="pt-5">
        <h3 class="caption font-black uppercase tracking-widest font-mono mb-3.5">
          {{ 'unitDetails.info.amenities' | translate }}
        </h3>

        <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
          @for (amenity of amenities; track amenity) {
            <div
              class="flex items-center gap-2.5 px-3 py-2 bg-bg-secondary border border-border-subtle rounded-xl hover:border-border transition-all duration-150"
            >
              <div
                class="w-5 h-5 rounded-md bg-success/10 flex items-center justify-center text-success shadow-sm shrink-0"
              >
                <i class="fa-solid fa-circle-check text-[10px]"></i>
              </div>
              <span class="text-text-secondary font-bold text-xs sm:text-sm break-words min-w-0">{{ amenity }}</span>
            </div>
          }
        </div>
      </div>
    </div>
  `,
})
export class UnitInfoComponent {
  @Input() title: string = '';
  @Input() location: string = '';
  @Input() bedrooms: number = 0;
  @Input() bathrooms: number = 0;
  @Input() area: string = '';
  @Input() rating: number = 0;
  @Input() reviews: number = 0;
  @Input() floor: number | string = '';
  @Input() furnished: boolean = false;
  @Input() yearBuilt: number | null = null;
  @Input() description: string = '';
  @Input() amenities: string[] = [];
  @Input() unitType: string = '';
  @Input() unitNumber: string = '';
  @Input() adults: number = 0;
  @Input() children: number = 0;
}



